import { useEffect, useRef, useCallback } from "react";
import { twMerge } from "tailwind-merge";
import { Mail01, Phone } from "untitledui-js/react";
import { useTranslation } from "react-i18next";
import { Button, Typography } from "@pondigitalsolutions/pon-component-library";

import { PeopleFinderPerson } from "@hooks/peopleFinder/usePeopleFinder";
import { useDrawerScrollLock } from "@hooks/useDrawerScrollLock";
import { escapeVCardValue, generateVCard } from "@utils/generateVCard";
import { downloadVCard } from "@utils/downloadVCard";

const COMMON_BUTTON_CLASSES = "h-10 w-10 cursor-pointer rounded-full p-0";

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"]):not([disabled])';

interface PeopleFinderDetailsProps {
  isOpen: boolean;
  person: PeopleFinderPerson | null;
  closeDetails: () => void;
}

export const PeopleFinderDetails = ({
  isOpen,
  person,
  closeDetails,
}: PeopleFinderDetailsProps) => {
  const { t } = useTranslation();

  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const drawerRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useDrawerScrollLock(isOpen);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        closeDetails();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, closeDetails]);

  // Focus trap: capture previous focus, move focus into drawer, and trap Tab
  const handleFocusTrap = useCallback((event: KeyboardEvent) => {
    if (event.key !== "Tab" || !drawerRef.current) return;

    const focusableElements =
      drawerRef.current.querySelectorAll(FOCUSABLE_SELECTOR);
    const focusableArray = Array.from(focusableElements) as HTMLElement[];
    const firstElement = focusableArray[0];
    const lastElement = focusableArray.at(-1);

    if (focusableArray.length === 0) {
      event.preventDefault();
      return;
    }

    if (event.shiftKey) {
      if (
        document.activeElement === firstElement ||
        !drawerRef.current.contains(document.activeElement)
      ) {
        event.preventDefault();
        lastElement?.focus();
      }
    } else if (
      document.activeElement === lastElement ||
      !drawerRef.current.contains(document.activeElement)
    ) {
      event.preventDefault();
      firstElement?.focus();
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      // Store the element that had focus before opening
      previousFocusRef.current = document.activeElement as HTMLElement;

      // Focus the close button after the drawer transition starts
      requestAnimationFrame(() => {
        const closeButton = drawerRef.current?.querySelector(
          "#close-button"
        ) as HTMLElement | null;
        closeButton?.focus();
      });

      document.addEventListener("keydown", handleFocusTrap);
    } else {
      document.removeEventListener("keydown", handleFocusTrap);

      // Restore focus to the previously focused element
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    }

    return () => {
      document.removeEventListener("keydown", handleFocusTrap);
    };
  }, [isOpen, handleFocusTrap]);

  const contactDetails = person
    ? [
        {
          label: person.opcoEmailAccount,
          href: `mailto:${person.opcoEmailAccount}`,
          icon: <Mail01 className="h-2.5 w-2.5 stroke-secondary-700" />,
          ariaLabel: t("PeopleFinder.emailPerson", {
            name: `${person.firstName} ${person.lastName}`,
            email: person.opcoEmailAccount,
          }),
        },
        {
          label: person.ponMobilePhone,
          href: `tel:${person.ponMobilePhone}`,
          icon: <Phone className="h-2.5 w-2.5 stroke-secondary-700" />,
          ariaLabel: t("PeopleFinder.callPerson", {
            name: `${person.firstName} ${person.lastName}`,
            phone: person.ponMobilePhone,
          }),
        },
      ]
    : [];

  return (
    <>
      <div
        className={twMerge(
          "fixed inset-0 z-70 bg-black/15 backdrop-blur-xs transition-opacity duration-300",
          isOpen
            ? "pointer-events-auto opacity-100"
            : "pointer-events-none opacity-0"
        )}
        onClick={closeDetails}
        aria-hidden="true"
      />
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Person details"
        className={twMerge(
          "fixed top-0 right-0 z-80 h-full w-full overflow-y-auto overscroll-contain bg-white p-6 lg:w-128 dark:bg-secondary-950",
          isOpen ? "translate-x-0" : "translate-x-full",
          // We don't add the transition on iOS devices.
          // Reason is because on iOS the navigation won't be displayed after opening it, clicking on the Okta button and then opening it again.
          // This is an issue that seems to have something to do with position fixed and transform.
          isIos ? "" : "transform transition-transform duration-300 ease-in-out"
        )}
      >
        {person && (
          <div className="flex flex-col gap-6">
            <div className="flex justify-between">
              <Button
                id="close-button"
                variant="primary"
                startIcon="ArrowLeft"
                onClick={closeDetails}
                className={COMMON_BUTTON_CLASSES}
                size="lg"
                aria-label="Close details"
              />
              <Button
                id="save-contact"
                variant="secondary"
                size="xl"
                endIcon="UserCircle"
                onClick={() => {
                  const vcardString = generateVCard(person);
                  downloadVCard(
                    vcardString,
                    `${escapeVCardValue(person.firstName)}_${escapeVCardValue(person.lastName)}.vcf`
                  );
                }}
                className="gap-1"
              >
                {t("PeopleFinder.save")}
              </Button>
            </div>

            <div className="flex flex-col items-center justify-center gap-4">
              <div className="flex aspect-square h-35 w-35 items-center justify-center rounded-full bg-secondary-700">
                <Typography size="5xl" className="font-semibold text-white">
                  {person.firstName.charAt(0)}
                </Typography>
              </div>
              <Typography
                size="4xl"
                className="text-center leading-[120%] font-semibold text-secondary-700 dark:text-white"
              >
                {`${person.firstName} ${person.lastName}`}
              </Typography>
              <Typography
                size="base"
                className="text-center leading-[120%] font-normal text-secondary-700 dark:text-tertiary-100"
              >
                {person.opcoFunctionTitle} {t("PeopleFinder.at")}{" "}
                {person.opcoLevel}
                <br />
                {person.ponCluster}
              </Typography>
              <div className="flex gap-4">
                {person.ponMobilePhone && (
                  <Button
                    variant="primary"
                    id="call-button"
                    startIcon="Phone"
                    size="lg"
                    className={COMMON_BUTTON_CLASSES}
                    href={`tel:${person.ponMobilePhone}`}
                    ariaLabel={t("PeopleFinder.callPerson", {
                      name: `${person.firstName} ${person.lastName}`,
                      phone: person.ponMobilePhone,
                    })}
                  />
                )}
                {person.opcoEmailAccount && (
                  <Button
                    variant="primary"
                    id="email-button"
                    startIcon="Mail01"
                    size="lg"
                    className={COMMON_BUTTON_CLASSES}
                    href={`mailto:${person.opcoEmailAccount}`}
                    ariaLabel={t("PeopleFinder.emailPerson", {
                      name: `${person.firstName} ${person.lastName}`,
                      email: person.opcoEmailAccount,
                    })}
                  />
                )}
              </div>
            </div>

            <div className="flex flex-col gap-4 rounded-xl bg-tertiary-50 p-6 dark:bg-secondary-900">
              <Typography
                size="lg"
                className="leading-[120%] font-semibold text-secondary-700 dark:text-white"
              >
                {t("PeopleFinder.contactDetails")}
              </Typography>
              <div className="flex flex-col gap-2.5">
                {contactDetails.map((detail) => {
                  if (!detail.label) return null;

                  return (
                    <div className="flex items-center gap-1" key={detail.label}>
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100">
                        {detail.icon}
                      </div>
                      <a
                        href={detail.href}
                        className="font-light text-tertiary-900 dark:text-tertiary-100"
                        aria-label={detail.ariaLabel}
                      >
                        {detail.label}
                      </a>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
